<template>
  <div
    :class="[
'rounded-2xl text-center my-3 mx-2 shadow bg-prediction-card transition duration-300 relative',
      borderStyle,
    ]"
  >
    <!-- Placeholder: one or both teams not yet determined -->
    <div v-if="isPlaceholder" class="flex justify-evenly items-center py-5 px-3">
      <PredictionChoiceTeam
        v-if="match.teamHome"
        class="w-1/3"
        options="h-12 w-12"
        :team="match.teamHome"
        status="default"
        :clickable="false"
        :greyOut="false"
        :chosen="false"
      />
      <div v-else class="w-1/3 flex flex-col items-center gap-1 opacity-40">
        <div class="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">
          <span class="text-white text-lg font-bold">?</span>
        </div>
        <p class="text-xs text-white">TBD</p>
      </div>
      <p class="w-1/3 text-white/40 text-sm font-semibold">vs</p>
      <PredictionChoiceTeam
        v-if="match.teamAway"
        class="w-1/3"
        options="h-12 w-12"
        :team="match.teamAway"
        status="default"
        :clickable="false"
        :greyOut="false"
        :chosen="false"
      />
      <div v-else class="w-1/3 flex flex-col items-center gap-1 opacity-40">
        <div class="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">
          <span class="text-white text-lg font-bold">?</span>
        </div>
        <p class="text-xs text-white">TBD</p>
      </div>
    </div>

    <div v-else class="flex align justify-evenly items-center">
      <PredictionChoiceTeam
        class="w-1/3"
        :options="
          madePrediction && match.prediction.choice === 'home'
            ? 'h-14 w-14'
            : 'h-12 w-12'
        "
        :team="match.teamHome"
        :status="status('home')"
        :clickable="!disabled"
        :greyOut="
          madePrediction &&
          match.prediction.choice !== 'home' &&
          match.status !== 'started'
        "
        :chosen="madePrediction && match.prediction.choice === 'home'"
        @click.native="setPrediction('home')"
      />
      <div
        v-if="match.groupId"
        class="flex flex-col my-2 items-center justify-start px-3 h-full w-1/3"
      >
        <p class="text-md text-red-300" v-if="finished && !madePrediction">
          No prediction made
        </p>

        <div class="flex-grow mt-3">
          <PredictionChoiceDraw
            options="h-12 w-12"
            :status="status('draw')"
            :clickable="!disabled"
            :greyOut="
              madePrediction &&
              match.prediction.choice !== 'draw' &&
              match.status !== 'started'
            "
            :teamHome="match.teamHome"
            :teamAway="match.teamAway"
            :chosen="madePrediction && match.prediction.choice === 'draw'"
            @click.native="setPrediction('draw')"
          />
        </div>
      </div>
      <PredictionChoiceTeam
        class="w-1/3"
        :options="
          madePrediction && match.prediction.choice === 'away'
            ? 'h-14 w-14'
            : 'h-12 w-12'
        "
        :team="match.teamAway"
        :status="status('away')"
        :clickable="!disabled"
        :greyOut="
          madePrediction &&
          match.prediction.choice !== 'away' &&
          match.status !== 'started'
        "
        :chosen="madePrediction && match.prediction.choice === 'away'"
        @click.native="setPrediction('away')"
      />
    </div>
    <CornerPoints v-if="finished && madePrediction" :correct="correctPrediction" />
    <div v-if="predictions">
      <p class="border-t border-b py-3 my-3 text-xs text-gray-400">{{
        matchDate
      }}</p>
      <MatchPredictions :predictions="predictions" :match="match" />
    </div>
    <div
      v-if="!predictions || (match.location && match.status !== 'started')"
      class="flex items-center justify-center p-2 rounded-b-2xl bg-prediction-info"
    >
      <p v-if="!predictions" class="text-xs text-white/70">
        <BaseIcon
          v-if="match.status === 'started'"
          name="circle"
          fade
          class="mr-1 text-green-400"
        />
        {{ matchDate }}
        <span class="ml-1 font-bold" v-if="match.minute"> {{ match.minute }}'</span>
      </p>
      <p
        v-if="match.location && match.status !== 'started'"
        class="ml-4 text-white/70 text-xs"
      >
        <BaseIcon name="map-pin" class="font-bold text-white" /> {{ match.location }}
      </p>
      <p v-if="match.groupName" class="ml-4 text-white/70 text-xs">
        <BaseIcon name="people-group" class="font-bold text-white" /> {{ match.groupName }}
      </p>
    </div>
  </div>
</template>

<script>
import PredictionChoiceTeam from './PredictionChoiceTeam'
import PredictionChoiceDraw from './PredictionChoiceDraw'
import MatchPredictions from './MatchPredictions'
import CornerPoints from './CornerPoints'
import {
  formatTime,
  homeTeamWon,
  awayTeamWon,
  isPlaceholderMatch,
} from '@/utils/helpers'

export default {
  components: {
    PredictionChoiceTeam,
    PredictionChoiceDraw,
    CornerPoints,
    MatchPredictions,
  },

  props: {
    match: {
      type: Object,
      required: true,
    },
    selectable: {
      type: Boolean,
      default: true,
    },
    predictions: {
      type: Object,
      required: false,
    },
  },

  methods: {
    async setPrediction(choice) {
      if (this.disabled) return

      this.loading = true
      const prediction = await this.$store.dispatch(`matches/setPrediction`, {
        match: this.match,
        choice,
      })
      this.$set(this.match, 'prediction', prediction)
      this.loading = false
    },
    status(choice) {
      if (this.madePrediction && this.match.prediction.choice === choice) {
        if (this.match.status === 'finished') {
          return this.correctPrediction ? 'correct' : 'wrong'
        } else {
          return 'selected'
        }
      } else {
        return 'default'
      }
    },
    formatTime,
    homeTeamWon,
    awayTeamWon,
  },

  computed: {
    isPlaceholder() {
      return isPlaceholderMatch(this.match)
    },
    disabled() {
      return !this.selectable || this.loading || this.isPlaceholder
    },
    finished() {
      return this.match.status === 'finished'
    },
    matchDate() {
      if (this.match.status === 'finished') {
        return 'Full Time'
      } else if (this.match.status === 'started') {
        return 'Live'
      } else {
        return this.formatTime(this.match.kickoffTime)
      }
    },
    madePrediction() {
      return 'prediction' in this.match
    },
    correctPrediction() {
      return (
        this.match.status === 'finished' &&
        this.madePrediction &&
        ((this.match.prediction.choice === 'draw' &&
          this.match.teamAway.score === this.match.teamHome.score) ||
          (this.match.prediction.choice === 'away' &&
            this.awayTeamWon(this.match)) ||
          (this.match.prediction.choice === 'home' &&
            this.homeTeamWon(this.match)))
      )
    },

    borderStyle() {
      if (this.match.status === 'finished') {
        // Game finished - Didn't make a prediction
        if (!this.madePrediction) {
          return 'border-prediction-default'
        }
        // Game finished - Prediction is either right or wrong/missing
        return this.correctPrediction
          ? 'border-prediction-correct border-opacity-20'
          : 'border-prediction-wrong border-opacity-20'
      } else if (this.match.status === 'upcoming' && !this.madePrediction) {
        // Game upcoming and no prediction made
        return 'border-prediction-default'
      } else {
        // Game upcoming and prediction already made
        return ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.bg-prediction-card {
  background-color: rgba(255, 255, 255, 0.08);
}
.bg-prediction-info {
  background-color: rgba(255, 255, 255, 0.04);
}
</style>
